import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useReactSimpleMatchMedia from "react-simple-matchmedia";
import { Breadcrumb, Chip, Icon, Pagination, SearchSummary, SectionHeader, SegmentedControl } from "@zorni/components-lib";
import ListCard from "../../components/trips/listCard/ListCard";
import SearchForm from "../../components/searshForms/searchForm/SearchForm";
import useIntersection from "../../hooks/useIntersection";
import { useHeaderDock } from "../../providers/HeaderDockContext";
import { useSearch } from "../../hooks/SearchContext";
import EmptyResult from "../../components/trips/emptyResult/EmptyResult";
import ResultsFilters, {
  durationLabel,
} from "../../components/searchResults/resultsFilters/ResultsFilters";
import { useSearchResults } from "./useSearchResults";
import { SORTS, formatAmount, parseDate } from "./searchResults";
import styles from "./TravelResult.module.css";

const SORT_FALLBACKS = {
  relevance: "Pertinence",
  "price-asc": "Prix croissant",
  "price-desc": "Prix décroissant",
  "duration-asc": "Durée croissante",
  "duration-desc": "Durée décroissante",
};

/**
 * Page résultats — « 02 - Résultats de recherche ».
 *
 * En haut : le formulaire de recherche (destination, dates, voyageurs),
 * pré-rempli depuis l'URL (sur téléphone : récapitulatif qui ouvre la feuille
 * de recherche). Quand il sort de l'écran, la version compacte prend le
 * relais dans le header, comme sur l'accueil. Puis : filtres (durée,
 * budget), tri, affichage grille/liste, filtres actifs et pagination.
 */
const TravelResult = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  // Même seuil que .layout dans le CSS : en dessous, les filtres passent en tiroir.
  const isDrawer = useReactSimpleMatchMedia("(max-width: 959.98px)");
  // Téléphone : récapitulatif + feuille de recherche au lieu du formulaire.
  const isPhone = useReactSimpleMatchMedia("(max-width: 599.98px)");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);

  const {
    query,
    durations,
    maxPrice,
    sort,
    view,
    page,
    totalPages,
    loading,
    total,
    pageItems,
    priceBounds,
    suggestions,
    update,
  } = useSearchResults();

  useEffect(() => {
    if (!isDrawer) setFiltersOpen(false);
  }, [isDrawer]);

  /* Le formulaire et la barre du header partagent l'état de recherche : on
     le recale sur l'URL à chaque nouvelle recherche. */
  const { search, updateSearch, setSheetOpen } = useSearch();
  useEffect(() => {
    updateSearch({
      destination: query.destination,
      startDate: query.startDate,
      endDate: query.endDate,
      adults: query.adult,
      children: query.child,
      babies: query.baby,
    });
  }, [
    updateSearch,
    query.destination,
    query.startDate,
    query.endDate,
    query.adult,
    query.child,
    query.baby,
  ]);

  /* Barre compacte dans le header dès que le formulaire n'est plus visible. */
  const isSearchVisible = useIntersection(searchRef, { threshold: 0.2 });
  const { setDocked } = useHeaderDock();
  useEffect(() => {
    setDocked(!isSearchVisible);
    return () => setDocked(false);
  }, [isSearchVisible, setDocked]);

  /* ----------------------------------------------------------- Libellés */
  const formatDay = (value) =>
    parseDate(value)?.toLocaleDateString(locale, { day: "numeric", month: "short" }) || "";

  const datesLabel = (() => {
    if (query.startDate && query.endDate) return `${formatDay(query.startDate)} → ${formatDay(query.endDate)}`;
    if (query.startDate) return `${t("trips.results.from", "À partir du")} ${formatDay(query.startDate)}`;
    if (query.endDate) return `${t("trips.results.until", "Jusqu'au")} ${formatDay(query.endDate)}`;
    return "";
  })();

  const plural = (n, key, one, many) => `${n} ${t(`trips.results.${n > 1 ? `${key}Plural` : key}`, n > 1 ? many : one)}`;
  const travelersLabel = [
    query.adult > 0 && plural(query.adult, "adult", "adulte", "adultes"),
    query.child > 0 && plural(query.child, "child", "enfant", "enfants"),
    query.baby > 0 && plural(query.baby, "baby", "bébé", "bébés"),
  ]
    .filter(Boolean)
    .join(" · ");

  const title = query.destination
    ? t("trips.results.titleWithDestination", {
        destination: query.destination,
        defaultValue: `Résultats pour « ${query.destination} »`,
      })
    : t("trips.results.title", "Résultats de recherche");

  const subtitle = [datesLabel || t("trips.results.flexibleDates", "Dates flexibles"), travelersLabel]
    .filter(Boolean)
    .join(" · ");

  const countLabel = loading
    ? t("trips.results.loading", "Recherche en cours…")
    : total === 0
    ? t("trips.results.countNone", "Aucun voyage trouvé")
    : total === 1
    ? t("trips.results.countOne", "1 voyage trouvé")
    : t("trips.results.countMany", { count: total, defaultValue: `${total} voyages trouvés` });

  /* -------------------------------------------------------- Filtres actifs */
  const currency = priceBounds?.currency || "";
  const activeFilters = [
    query.destination && {
      key: "destination",
      label: `${t("trips.results.chipDestination", "Destination")} : ${query.destination}`,
      remove: () => update({ destination: null }),
    },
    datesLabel && {
      key: "dates",
      label: `${t("trips.results.chipDates", "Dates")} : ${datesLabel}`,
      remove: () => update({ startDate: null, endDate: null }),
    },
    travelersLabel && {
      key: "travelers",
      label: `${t("trips.results.chipTravelers", "Voyageurs")} : ${travelersLabel}`,
      remove: () => update({ adult: null, child: null, baby: null }),
    },
    ...durations.map((d) => ({
      key: `duration-${d}`,
      label: `${t("trips.results.duration", "Durée")} : ${durationLabel(t, d)}`,
      remove: () => update({ duration: durations.filter((v) => v !== d) }),
    })),
    maxPrice > 0 && {
      key: "budget",
      label: `${t("trips.results.chipBudget", "Budget")} ≤ ${formatAmount(maxPrice, currency, locale)}`,
      remove: () => update({ maxPrice: null }),
    },
  ].filter(Boolean);

  const clearAll = () =>
    update({
      destination: null,
      startDate: null,
      endDate: null,
      adult: null,
      child: null,
      baby: null,
      duration: null,
      maxPrice: null,
    });

  const goToPage = (next) => {
    update({ page: next }, { keepPage: true });
    resultsRef.current?.scrollIntoView({ block: "start" });
  };

  const filters = (
    <ResultsFilters
      durations={durations}
      maxPrice={maxPrice}
      priceBounds={priceBounds}
      onChange={update}
      onReset={() => update({ duration: null, maxPrice: null })}
      drawer={isDrawer}
      open={filtersOpen}
      onClose={() => setFiltersOpen(false)}
    />
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* ------------------------------------------------------ En-tête */}
        <header className={styles.hero}>
          <Breadcrumb
            className={styles.breadcrumb}
            ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
            linkComponent={Link}
            items={[
              { label: t("trips.results.home", "Accueil"), to: "/" },
              { label: t("trips.results.title", "Résultats de recherche") },
            ]}
          />
          <SectionHeader size="page" className={styles.header} title={title} subtitle={subtitle} />
        </header>

        <div ref={searchRef} className={styles.search}>
          {isPhone ? (
            <SearchSummary value={search} onClick={() => setSheetOpen(true)} t={t} />
          ) : (
            <SearchForm value={search} onChange={updateSearch} />
          )}
        </div>

        <div className={styles.layout}>
          {isDrawer ? filters : <div className={styles.filtersCol}>{filters}</div>}

          {/* ------------------------------------------------- Résultats */}
          <section
            ref={resultsRef}
            className={styles.results}
            aria-label={t("trips.results.title", "Résultats de recherche")}
          >
            <div className={styles.toolbar}>
              <p className={styles.count} role="status" aria-live="polite">
                {countLabel}
              </p>

              <div className={styles.toolbarActions}>
                {/* Téléphone : bouton « Trier » compact posé sur le sélecteur natif
                    (invisible), pour garder Trier, affichage et Filtres sur une ligne. */}
                <label className={isPhone ? styles.sortCompact : styles.sort}>
                  {isPhone ? (
                    <span className={styles.sortCompactFace} aria-hidden="true">
                      <Icon name="iconSortAscending" size="sm" color="inherit" />
                      {t("trips.results.sort", "Trier")}
                      {sort !== "relevance" && <span className={styles.sortDot} />}
                    </span>
                  ) : (
                    <span className={styles.sortLabel}>{t("trips.results.sort", "Trier")}</span>
                  )}
                  <select
                    className={isPhone ? styles.sortCompactSelect : styles.sortSelect}
                    value={sort}
                    onChange={(e) => update({ sort: e.target.value })}
                    aria-label={isPhone ? t("trips.results.sort", "Trier") : undefined}
                  >
                    {SORTS.map((value) => (
                      <option key={value} value={value}>
                        {t(`trips.results.sorts.${value}`, SORT_FALLBACKS[value])}
                      </option>
                    ))}
                  </select>
                </label>

                <SegmentedControl
                  aria-label={t("trips.results.viewAria", "Mode d'affichage des résultats")}
                  value={view}
                  onChange={(value) => update({ view: value }, { keepPage: true })}
                  options={[
                    { value: "grid", iconName: "iconGrid", label: t("trips.results.viewGrid", "Affichage en grille") },
                    { value: "list", iconName: "iconListe", label: t("trips.results.viewList", "Affichage en liste") },
                  ]}
                />

                {isDrawer && (
                  <button
                    type="button"
                    className={styles.filtersToggle}
                    onClick={() => setFiltersOpen(true)}
                    aria-expanded={filtersOpen}
                    aria-controls="results-filters"
                  >
                    <Icon name="iconFilter" size="sm" color="inherit" />
                    {t("trips.results.filters", "Filtres")}
                    {durations.length + (maxPrice ? 1 : 0) > 0 && (
                      <span className={styles.filtersBadge}>{durations.length + (maxPrice ? 1 : 0)}</span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {activeFilters.length > 0 && (
              <div className={styles.activeFilters}>
                {activeFilters.map((f) => (
                  <Chip
                    key={f.key}
                    variant="filter"
                    label={f.label}
                    removeLabel={t("trips.results.removeFilter", {
                      label: f.label,
                      defaultValue: `Retirer le filtre ${f.label}`,
                    })}
                    onRemove={f.remove}
                  />
                ))}
                <Chip
                  variant="filter"
                  dashed
                  label={t("trips.results.clearAll", "Tout effacer")}
                  onRemove={clearAll}
                />
              </div>
            )}

            {!loading && total === 0 ? (
              <EmptyResult
                suggestions={suggestions}
                onSuggestion={(country) =>
                  update({ destination: country, startDate: null, endDate: null, duration: null, maxPrice: null })
                }
                onReset={clearAll}
              />
            ) : (
              <>
                <ListCard
                  items={pageItems}
                  view={view}
                  loading={loading}
                  adult={query.adult}
                  child={query.child}
                  baby={query.baby}
                />
                {!loading && totalPages > 1 && (
                  <Pagination
                    className={styles.pagination}
                    totalPages={totalPages}
                    currentPage={page}
                    onPageChange={goToPage}
                    aria-label={t("trips.results.paginationAria", "Pagination des résultats")}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TravelResult;
